/**
 * Blobs on canvas. Bodies and faces are blobatar's, turned into Path2D so they
 * live inside the isometric scene (behind benches, under the night wash).
 * Adapted from the Agent Office's blob renderer in observe-agents-do-things.
 */
import { _layout, _marks, type Mark } from 'blobatar/internal'
import type { Expression } from 'blobatar'
import { happy, idle, love, sad, scared, shy, sick, sleepy, smug, surprised, thinking, unsure, wink } from 'blobatar/expression'
import { HUE_STEP, SHAPES, TONES, type Look } from '../../../shared/cosmetics.ts'
import type { Emote } from '../../../shared/protocol.ts'
import { drawHat } from './hats.ts'

export const EXPRESSIONS: Record<Emote | 'idle', Expression> = {
  idle, happy, love, surprised, sad, smug, wink, shy, scared, thinking, sick, sleepy, unsure,
}

export interface BlobArt {
  body: Array<{ path: Path2D; fill: string }>
  eyes: Array<{ path: Path2D; fill: string; cx: number; cy: number }>
  dy: number
  frame: { cx: number; cy: number; rx: number; ry: number }
  bottom: number
  /** The main body colour, for name tags and UI accents. */
  color: string
}

const cache = new Map<string, BlobArt>()

function pathMaxY(d: string): number {
  let max = -Infinity, cmd = 'M'
  const nums: number[] = []
  const flush = () => {
    if (cmd === 'V') for (const v of nums) max = Math.max(max, v)
    else if (cmd !== 'H') for (let k = 1; k < nums.length; k += 2) max = Math.max(max, nums[k]!)
    nums.length = 0
  }
  for (const tok of d.match(/[A-Za-z]|-?[\d.]+(?:e-?\d+)?/g) ?? []) {
    if (/[A-Za-z]/.test(tok)) { flush(); cmd = tok.toUpperCase() } else nums.push(Number(tok))
  }
  flush()
  return Number.isFinite(max) ? max : 88
}

function toPath(m: Mark): Path2D {
  if (m.kind === 'circle') {
    const p = new Path2D()
    p.arc(m.cx, m.cy, m.r, 0, Math.PI * 2)
    return p
  }
  return new Path2D(m.d)
}

export function lookOptions(look: Look) {
  return { hue: look.hue * HUE_STEP, tone: TONES[look.tone]!.value, traits: { shape: SHAPES[look.shape]!.trait } }
}

/** Geometry for a look wearing an expression. Cached, so cheap per frame. */
export function blobArt(look: Look, emote: Emote | 'idle' = 'idle'): BlobArt {
  const key = `${look.seed}|${look.shape}|${look.hue}|${look.tone}|${emote}`
  const hit = cache.get(key)
  if (hit) return hit
  const opts = { ...lookOptions(look), expression: EXPRESSIONS[emote] }
  const m = _marks(look.seed, { ...opts, background: false })
  const lay = _layout(look.seed, opts)
  const n = lay.eyes.length
  const split = m.marks.length - n
  const body = m.marks.slice(0, split).map((mk) => ({ path: toPath(mk), fill: mk.fill }))
  const eyes = m.marks.slice(split).map((mk, i) => ({ path: toPath(mk), fill: mk.fill, cx: lay.eyes[i]!.cx, cy: lay.eyes[i]!.cy }))
  const dy = Number(/translate\(0 (-?[\d.]+)\)/.exec(m.transform)?.[1] ?? 0)
  const b = lay.body
  let bottom = 0
  for (const mk of m.marks.slice(0, split)) bottom = Math.max(bottom, mk.kind === 'circle' ? mk.cy + mk.r : pathMaxY(mk.d))
  const art: BlobArt = { body, eyes, dy, frame: { cx: b.cx, cy: b.cy, rx: b.rx, ry: b.ry }, bottom, color: body[0]?.fill ?? '#ccc' }
  if (cache.size > 800) cache.clear()
  cache.set(key, art)
  return art
}

export interface BlobDraw {
  /** Rendered height of the 100-unit viewBox, in px. */
  size: number
  facing: 1 | -1
  eyes?: number
  sx?: number
  sy?: number
  rot?: number
  dx?: number
  dyExtra?: number
  hat?: Look['hat']
  /** Seconds, for animated hats. */
  t?: number
}

/** Draw a blob with its bottom centre at (x, y) on the current transform. */
export function drawBlob(ctx: CanvasRenderingContext2D, art: BlobArt, x: number, y: number, o: BlobDraw): void {
  const s = o.size / 100
  ctx.save()
  ctx.translate(x, y)
  if (o.rot) ctx.rotate(o.rot * o.facing)
  ctx.scale(o.facing * s * (o.sx ?? 1), s * (o.sy ?? 1))
  ctx.translate(-50 + (o.dx ?? 0), -art.bottom + art.dy + (o.dyExtra ?? 0))
  for (const b of art.body) { ctx.fillStyle = b.fill; ctx.fill(b.path) }
  const f = art.frame
  // A soft highlight so the blob reads as round under the scene's key light.
  ctx.beginPath()
  ctx.ellipse(f.cx - f.rx * 0.32, f.cy - f.ry * 0.38, f.rx * 0.34, f.ry * 0.2, -0.6, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(255,255,255,0.28)'
  ctx.fill()
  const open = o.eyes ?? 1
  for (const e of art.eyes) {
    ctx.fillStyle = e.fill
    if (open >= 0.98) { ctx.fill(e.path); continue }
    ctx.save()
    ctx.translate(e.cx, e.cy)
    ctx.scale(1, Math.max(0.08, open))
    ctx.translate(-e.cx, -e.cy)
    ctx.fill(e.path)
    ctx.restore()
  }
  if (o.hat && o.hat !== 'none') drawHat(ctx, o.hat, art, o.t ?? 0)
  ctx.restore()
}

export interface Motion { sx: number; sy: number; rot: number; dx: number; dy: number; eyes: number }

export type BodyState = 'walk' | 'stand' | 'sit' | 'nap'

/**
 * Body motion. `t` in seconds, `phase` per blob, `act` is a one-shot
 * animation with its progress 0..1.
 */
export function blobMotion(state: BodyState, t: number, phase: number, act?: { a: 'hop' | 'spin' | 'wiggle'; k: number }, calm = false): Motion {
  const m: Motion = { sx: 1, sy: 1, rot: 0, dx: 0, dy: 0, eyes: 1 }
  const amp = calm ? 0.4 : 1
  const br = Math.sin(t * 2.1 + phase)
  m.sy += 0.02 * br * amp
  m.sx -= 0.014 * br * amp
  // Blink every few seconds, staggered per blob.
  const bl = (t * 0.31 + phase * 0.17) % 1
  if (bl < 0.035) m.eyes = Math.abs(bl / 0.0175 - 1)
  if (state === 'walk') {
    const k = Math.abs(Math.sin(t * 10 + phase))
    m.dy = -k * 8 * amp
    m.sy = 0.93 + 0.11 * k
    m.sx = 1.07 - 0.09 * k
    m.rot = 0.08
  } else if (state === 'sit') {
    m.sy = 0.9 + 0.015 * br
    m.sx = 1.07 - 0.01 * br
  } else if (state === 'nap') {
    m.sy = 0.8 + 0.03 * Math.sin(t * 1.4 + phase)
    m.sx = 1.12 + 0.02 * Math.sin(t * 1.4 + phase)
    m.eyes = 0.1
    m.dy = 2
  }
  if (act) {
    const k = act.k
    if (act.a === 'hop') {
      // Squash, launch, land.
      const air = Math.sin(Math.min(1, Math.max(0, (k - 0.15) / 0.7)) * Math.PI)
      m.dy -= air * 34
      const squash = k < 0.15 ? k / 0.15 : k > 0.85 ? (1 - k) / 0.15 : 0
      m.sy *= 1 - 0.18 * squash + 0.08 * air
      m.sx *= 1 + 0.14 * squash - 0.05 * air
    } else if (act.a === 'spin') {
      // Fake a turn by squeezing through zero width.
      m.sx *= Math.cos(k * Math.PI * 4)
      m.dy -= Math.sin(k * Math.PI) * 10
    } else {
      m.rot += Math.sin(k * Math.PI * 8) * 0.22 * (1 - k)
      m.dx += Math.sin(k * Math.PI * 8) * 4 * (1 - k)
    }
  }
  return m
}
